import React, { useState } from 'react';
import { Alert, Box, Button, Card, CardContent, Checkbox, FormControlLabel, Grid, TextField, Typography } from '@mui/material';
import { useNotify } from 'react-admin';
import { useNavigate } from 'react-router';
import { createEnterpriseBillingAccount } from '../../Services/billingAccount';
import { PRODUCT_LABELS } from '../../Services/productEntitlements';

const PRODUCTS = Object.keys(PRODUCT_LABELS);

/**
 * Création d'un compte ENTREPRISE, hors Stripe. Les quantités saisies sont celles du
 * contrat (Pennylane) : elles plafonnent, sauf « Illimité » coché (le compte peut alors dépasser). Le propriétaire reçoit une
 * invitation par email ; il entre ensuite par la porte unique et la retrouve.
 */
export const BillingAccountCreate: React.FC = () => {
	const notify = useNotify();
	const navigate = useNavigate();
	const [saving, setSaving] = useState(false);
	const [form, setForm] = useState({
		name: '',
		organisationName: '',
		siren: '',
		vat: '',
		adress: '',
		zipCode: '',
		city: '',
		country: 'FR',
		billEmail: '',
		signFirstname: '',
		signLastname: '',
		signEmail: '',
		signPhone: '',
		ownerEmail: '',
	});
	// Déclaratif : n'ouvre aucun droit, ne change rien à la facturation. Sert à
	// retrouver plus tard les comptes qu'on a créés pour une démo.
	const [isFree, setIsFree] = useState(false);
	const [quantities, setQuantities] = useState<Record<string, string>>(
		Object.fromEntries(PRODUCTS.map((p) => [p, '0']))
	);
	const [unlimited, setUnlimited] = useState<Record<string, boolean>>({});

	const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) =>
		setForm((f) => ({ ...f, [k]: e.target.value }));

	const valid = form.name.trim() !== '' && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.ownerEmail.trim());

	const submit = async () => {
		setSaving(true);
		try {
			const res = await createEnterpriseBillingAccount({
				...form,
				isFree,
				products: PRODUCTS.map((p) => ({ product: p, quantity: Math.max(0, Number(quantities[p]) || 0), unlimited: !!unlimited[p] })),
			});
			if (res.invitationError) {
				notify(`Compte créé, mais l'invitation n'est pas partie : ${res.invitationError}`, { type: 'warning' });
			} else {
				notify('Compte Entreprise créé, invitation envoyée au propriétaire', { type: 'success' });
			}
			navigate(`/billing-accounts/${res.billingAccount.id}`);
		} catch (e: any) {
			notify(e?.error || e?.message || 'Échec de la création', { type: 'error' });
		} finally {
			setSaving(false);
		}
	};

	const field = (label: string, k: keyof typeof form, props: Record<string, unknown> = {}) => (
		<TextField label={label} value={form[k]} onChange={set(k)} size="small" fullWidth {...props} />
	);

	return (
		<Box sx={{ p: 2 }}>
			<Card>
				<CardContent>
					<Typography variant="h6">Nouveau compte Entreprise</Typography>
					<Typography variant="caption" color="text.secondary">
						Hors Stripe : les quantités sont celles du contrat (indicatives, jamais bloquantes). Le propriétaire est invité par email.
					</Typography>

					<Typography variant="subtitle1" sx={{ mt: 3, mb: 1 }}>Société</Typography>
					<Grid container spacing={2}>
						<Grid item xs={12} md={6}>{field('Nom du compte *', 'name')}</Grid>
						<Grid item xs={12} md={6}>{field('Raison sociale', 'organisationName')}</Grid>
						<Grid item xs={6} md={3}>{field('SIREN', 'siren')}</Grid>
						<Grid item xs={6} md={3}>{field('TVA', 'vat')}</Grid>
						<Grid item xs={12} md={6}>{field('Email de facturation', 'billEmail', { type: 'email' })}</Grid>
						<Grid item xs={12} md={6}>{field('Adresse', 'adress')}</Grid>
						<Grid item xs={4} md={2}>{field('Code postal', 'zipCode')}</Grid>
						<Grid item xs={8} md={2}>{field('Ville', 'city')}</Grid>
						<Grid item xs={12} md={2}>{field('Pays (ISO)', 'country')}</Grid>
					</Grid>

					<Typography variant="subtitle1" sx={{ mt: 3, mb: 1 }}>Signataire</Typography>
					<Grid container spacing={2}>
						<Grid item xs={6} md={3}>{field('Prénom', 'signFirstname')}</Grid>
						<Grid item xs={6} md={3}>{field('Nom', 'signLastname')}</Grid>
						<Grid item xs={6} md={3}>{field('Email', 'signEmail', { type: 'email' })}</Grid>
						<Grid item xs={6} md={3}>{field('Téléphone', 'signPhone')}</Grid>
					</Grid>

					<Typography variant="subtitle1" sx={{ mt: 3, mb: 1 }}>Propriétaire</Typography>
					<Grid container spacing={2}>
						<Grid item xs={12} md={6}>{field('Email du propriétaire *', 'ownerEmail', { type: 'email' })}</Grid>
					</Grid>
					<Alert severity="info" sx={{ mt: 1 }}>
						Il reçoit une invitation. S'il n'a pas encore de compte, il le créera en se connectant (Google, Microsoft ou code par email) et retrouvera l'invitation.
					</Alert>

					<Typography variant="subtitle1" sx={{ mt: 3, mb: 1 }}>Nature du compte</Typography>
					<FormControlLabel
						control={<Checkbox checked={isFree} onChange={(e) => setIsFree(e.target.checked)} />}
						label="Compte gratuit (Free)"
					/>
					<Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
						Démo ou essai interne, pas un client — il ne paie pas. Sans effet sur les
						droits ni sur la facturation : sert à retrouver ces comptes au moment
						d'un nettoyage. Modifiable ensuite sur la fiche.
					</Typography>

					<Typography variant="subtitle1" sx={{ mt: 3, mb: 1 }}>Produits du contrat</Typography>
					<Grid container spacing={2}>
						{PRODUCTS.map((p) => (
							<Grid item xs={6} md={2} key={p}>
								<TextField
									label={`${PRODUCT_LABELS[p].label} (${PRODUCT_LABELS[p].unit})`}
									type="number"
									size="small"
									fullWidth
									inputProps={{ min: 0 }}
									value={quantities[p]}
									onChange={(e) => setQuantities((q) => ({ ...q, [p]: e.target.value }))}
								/>
								<FormControlLabel
									control={
										<Checkbox
											size="small"
											checked={!!unlimited[p]}
											onChange={(e) => setUnlimited((s) => ({ ...s, [p]: e.target.checked }))}
										/>
									}
									label={<Typography variant="caption">Illimité</Typography>}
								/>
							</Grid>
						))}
					</Grid>

					<Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 3 }}>
						<Button variant="outlined" onClick={() => navigate('/billing-accounts')} disabled={saving}>
							Annuler
						</Button>
						<Button variant="contained" onClick={submit} disabled={!valid || saving}>
							Créer le compte
						</Button>
					</Box>
				</CardContent>
			</Card>
		</Box>
	);
};
